import React, {useState, useEffect, useRef} from 'react'
import { Radio, Row, Button, Popover, Modal, Tooltip, Input, Slider } from 'antd'
import { EditOutlined, CaretUpOutlined, CaretDownOutlined } from '@ant-design/icons'
import SiloSettings from './SiloSettings';
import TripleToggleSwitch from '../../../utils/triple-toggle-switch';
import './SiloSnapshotModal.scss';

export default function SiloSnapshotModal(props) {
    
    const [editCameraName, setEditCameraName] = useState(false);
    const [cameraName, setCameraName] = useState('');
    const [showScalePanel, setShowScalePanel] = useState(false);
    const [scaleValues, setScaleValues] = useState({ x: 100, y: 100, z: 100 });
    const [rotationValues, setRotationValues] = useState({ x: 0, y: 0, z: 0 });
    const [positionValues, setPositionValues] = useState({ x: 0, y: 0, z: 0 });
    const [activeTransformMode, setActiveTransformMode] = useState(null);
    const [uniformScale, setUniformScale] = useState(false);
    const [transformMode, setTransformMode] = useState('left'); // 'left' = position, 'center' = rotation, 'right' = scale
    const [activeControl, setActiveControl] = useState(null); // Track which control is currently active: 'grid', 'safeZone', 'transform', 'cameraAlign', 'cameraControls'
    const [switchKey, setSwitchKey] = useState(0); // Key to force TripleToggleSwitch re-render
    const [isGridActive, setIsGridActive] = useState(false); // Independent grid highlight state
    const isInputFocusedRef = useRef(false); // Suppress transform callback while typing in inputs

    const onTransformInputFocus = () => {
        isInputFocusedRef.current = true;
        // Prevent TransformControls from stealing focus via domElement.focus() on pointerUp
        if (props.inspector && props.inspector.transformControls) {
            props.inspector.transformControls.enabled = false;
        }
    };

    const onTransformInputBlur = () => {
        isInputFocusedRef.current = false;
        // Re-enable transform controls so the gizmo is interactive again
        if (props.inspector && props.inspector.transformControls) {
            props.inspector.transformControls.enabled = true;
        }
    };
    
    const onCancelModal = () => {
        setShowScalePanel(false);
        setActiveTransformMode('translate');
        setTransformMode('left');
        if (props.inspector) {
            props.inspector.disableTransformControls();
        }
        props.resetZoom();
        props.setCameraSelected(false)
        props.setTakeSiloShotModal(false);
        props.changePerspective(props.perspective);
    }

    const handleTransformModeChange = (value) => {
        setTransformMode(value);

        if (props.inspector) {
            if (value === 'left') {
                // Position/Translate mode
                props.inspector.enableTransformControls('translate');
                setActiveTransformMode('translate');
            } else if (value === 'center') {
                // Rotate mode
                props.inspector.enableTransformControls('rotate');
                setActiveTransformMode('rotate');
            } else if (value === 'right') {
                // Scale mode
                props.inspector.disableTransformControls();
                setActiveTransformMode('scale');
            }
        }
    }

    const handleScaleChange = (axis, value) => {
        // Clamp value between 0 and 200
        const clampedValue = Math.max(0, Math.min(200, value));

        let newScaleValues;
        if (uniformScale) {
            // Apply same value to all axes
            newScaleValues = { x: clampedValue, y: clampedValue, z: clampedValue };
        } else {
            newScaleValues = { ...scaleValues, [axis]: clampedValue };
        }

        setScaleValues(newScaleValues);

        if (props.inspector && props.inspector.model) {
            props.inspector.model.scale.set(
                newScaleValues.x / 100,
                newScaleValues.y / 100,
                newScaleValues.z / 100
            );
        }
    }

    const handleRotationChange = (axis, value) => {
        // Allow values beyond 360 without wrapping — clamp to 0-360 range only on blur
        let normalizedValue = value;
        if (normalizedValue < 0) normalizedValue = 0;
        if (normalizedValue > 360) normalizedValue = 360;

        const newRotationValues = { ...rotationValues, [axis]: normalizedValue };
        setRotationValues(newRotationValues);

        if (props.inspector && props.inspector.model) {
            props.inspector.model.rotation.order = 'YXZ';
            props.inspector.model.rotation[axis] = (normalizedValue * Math.PI) / 180;
            // Sync quaternion-based rotation tracking with UI values
            props.inspector.syncTrackedRotation(newRotationValues);
        }
    }

    const handlePositionChange = (axis, value) => {
        const newPositionValues = { ...positionValues, [axis]: value };
        setPositionValues(newPositionValues);

        if (props.inspector && props.inspector.model) {
            // Convert inches to meters (Three.js units): 1 inch = 1/39.3701 meters
            props.inspector.model.position[axis] = value / 39.3701;
        }
    }

    const handleResetTransform = () => {
        if (props.inspector) {
            props.inspector.resetProductTransform();

            // Read back the actual values from the model after reset
            if (props.inspector.model) {
                // Update scale values
                const newScale = {
                    x: Math.round(props.inspector.model.scale.x * 100),
                    y: Math.round(props.inspector.model.scale.y * 100),
                    z: Math.round(props.inspector.model.scale.z * 100)
                };
                setScaleValues(newScale);

                // Update rotation values (convert from radians to degrees)
                const newRotation = {
                    x: Math.round((props.inspector.model.rotation.x * 180) / Math.PI),
                    y: Math.round((props.inspector.model.rotation.y * 180) / Math.PI),
                    z: Math.round((props.inspector.model.rotation.z * 180) / Math.PI)
                };
                setRotationValues(newRotation);

                // Update position values (convert meters to inches for UI display)
                const newPosition = {
                    x: parseFloat((props.inspector.model.position.x * 39.3701).toFixed(1)),
                    y: parseFloat((props.inspector.model.position.y * 39.3701).toFixed(1)),
                    z: parseFloat((props.inspector.model.position.z * 39.3701).toFixed(1))
                };
                setPositionValues(newPosition);
            }

            // Use handleTransformModeChange to properly update both state and UI
            handleTransformModeChange('left');

            // Force TripleToggleSwitch to re-render with correct position
            setSwitchKey(prev => prev + 1);
        }
    }

    const handleResetPosition = () => {
        if (props.inspector) {
            props.inspector.resetPosition();
        }
    }

    const handleResetRotation = () => {
        if (props.inspector) {
            props.inspector.resetRotation();
        }
    }

    const handleResetScale = () => {
        if (props.inspector) {
            props.inspector.resetScale();
        }
    }

    // Helper function to close all controls
    const closeAllControls = () => {
        // Close transform panel
        if (showScalePanel) {
            setShowScalePanel(false);
            if (props.inspector) {
                props.inspector.disableTransformControls();
            }
        }
        setActiveControl(null);
    }

    const handleToggleTransformPanel = () => {
        const newState = !showScalePanel;

        if (newState) {
            // Close all other controls first
            closeAllControls();
            // Close camera controls if open
            if (props.cameraSelected) {
                props.changeCameraState();
            }
            // Open transform panel
            setShowScalePanel(true);
            setActiveControl('transform');
            if (props.inspector) {
                props.inspector.enableTransformControls('translate');
                setActiveTransformMode('translate');
                setTransformMode('left');
            }
        } else {
            // Close transform panel
            setShowScalePanel(false);
            setActiveControl(null);
            if (props.inspector) {
                props.inspector.disableTransformControls();
                setActiveTransformMode(null);
            }
        }
    }

    const transformLabels = {
        left: {
            title: "Position",
            value: "left"
        },
        center: {
            title: "Rotation",
            value: "center"
        },
        right: {
            title: "Scaling",
            value: "right"
        }
    };

    // Set up transform change callback to sync on-screen controls with sliders
    useEffect(() => {
        if (props.inspector) {
            props.inspector.setTransformChangeCallback((type, values) => {
                if (isInputFocusedRef.current) return; // Don't overwrite while user is typing
                if (type === 'position') {
                    setPositionValues(values);
                } else if (type === 'rotation') {
                    // Normalize rotation values to 0-360 range
                    const normalizedValues = {
                        x: ((values.x % 360) + 360) % 360,
                        y: ((values.y % 360) + 360) % 360,
                        z: ((values.z % 360) + 360) % 360
                    };
                    setRotationValues(normalizedValues);
                } else if (type === 'scale') {
                    setScaleValues(values);
                }
            });
        }
    }, [props.inspector]);

    useEffect(() => {
        if (props.takeSiloShotModal){
            const timeout = setTimeout(() => {
                if(document.getElementById("silo-inspector-modal") != undefined && document.getElementById("silo-inspector")!= undefined){
                    document.getElementById("silo-inspector").prepend(document.getElementById("silo-inspector-child"));
                    document.getElementById("silo-inspector-modal").appendChild(document.getElementById("silo-inspector"));
                    document.getElementById("silo-inspector").style.zIndex = 2;
                    document.getElementById("silo-inspector").style.position = 'relative';
                }
            },0);
            return () => clearTimeout(timeout);
        }
        else {
            // Cleanup when modal closes
            setShowScalePanel(false);
            setActiveTransformMode('translate');
            setTransformMode('left');
            if (props.inspector) {
                props.inspector.disableTransformControls();
            }

            if(document.getElementById("silo-inspector-main") != undefined && document.getElementById("silo-inspector")!= undefined && document.getElementById("silo-inspector-modal") != undefined) {
                document.getElementById("silo-inspector-modal").prepend(document.getElementById("silo-inspector-child"));
                document.getElementById("silo-inspector-main").appendChild(document.getElementById("silo-inspector"));
                document.getElementById("silo-inspector").style.zIndex = -10;
                document.getElementById("silo-inspector").style.position = 'fixed';
                document.getElementById('silo-inspector').style.top = "1"
            }
        }
    },[props])
    
  return (
    <div>
        <Modal
            className ='take-silo-shot-modal'
            visible = {props.takeSiloShotModal}
            centered = {true}
            onCancel = { () => onCancelModal()}
            footer = {
                <div className="silo-modal-footer">
                    {/* Unified bottom controls bar */}
                    <div className="silo-unified-controls-wrapper">
                        <div className="silo-bottom-controls-container" data_tut="reactour__panrotate">

                            {/* Rotate button */}
                            <Popover placement="top" content={"Rotate"}>
                                <span
                                    className={`silo-control-icon ${!props.panToggle ? 'active' : ''} ${showScalePanel ? 'disabled-icon' : ''}`}
                                    onClick={() => {
                                        if (showScalePanel) {
                                            setShowScalePanel(false);
                                            if (props.inspector) props.inspector.disableTransformControls();
                                        }
                                        if (props.panToggle) props.onChangeToggle({ target: { value: 'rotate' } });
                                    }}
                                >
                                    <img src="/img/rotate_default.png" className="silo-bottom-icon-img silo-primary-icon" alt="Rotate" />
                                </span>
                            </Popover>

                            <div className="silo-bottom-separator" />

                            {/* Pan button */}
                            <Popover placement="top" content={"Pan"}>
                                <span
                                    className={`silo-control-icon ${props.panToggle ? 'active' : ''} ${showScalePanel ? 'disabled-icon' : ''}`}
                                    onClick={() => {
                                        if (showScalePanel) {
                                            setShowScalePanel(false);
                                            if (props.inspector) props.inspector.disableTransformControls();
                                        }
                                        if (!props.panToggle) props.onChangeToggle({ target: { value: 'pan' } });
                                    }}
                                >
                                    <img src="/img/pan_default.png" className="silo-bottom-icon-img silo-primary-icon" alt="Pan" />
                                </span>
                            </Popover>

                            <div className="silo-bottom-separator" />

                            {/* Zoom In button */}
                            <Popover placement="top" content={"Zoom In"}>
                                <span className="silo-control-icon" onClick={() => props.zoomIn()}>
                                    <img src="/img/SiloZoomIn.png" className="silo-bottom-icon-img" alt="Zoom In" />
                                </span>
                            </Popover>

                            <div className="silo-bottom-separator" />

                            {/* Zoom Out button */}
                            <Popover placement="top" content={"Zoom Out"}>
                                <span className="silo-control-icon" onClick={() => props.zoomOut()}>
                                    <img src="/img/SiloZoomOut.png" className="silo-bottom-icon-img" alt="Zoom Out" />
                                </span>
                            </Popover>

                        </div>
                    </div>

                    {/* Right-aligned Action Buttons */}
                    <div className='silo-snapshot-settings-buttons silo-action-buttons'>
                        <Button className="modal-okay-gray square font-14 silo-action-btn" onClick={() => onCancelModal()}>
                            Cancel
                        </Button>
                        <Button className="modal-okay square font-14 silo-action-btn" onClick={() => {
                            // Hide transform controls so they don't appear in the snapshot
                            if (showScalePanel) {
                                setShowScalePanel(false);
                                if (props.inspector) {
                                    props.inspector.disableTransformControls();
                                }
                                setActiveTransformMode(null);
                            }
                            props.setCameraSelected(false)
                            if(!props.editCamera) {
                                props.snapshotTaken()
                            }
                            else {
                                props.snapshotUpdate()
                            }
                            props.resetZoom();
                        }}>
                            {!props.editCamera? 'Create' : 'Update'}
                        </Button>
                    </div>
                </div>
            }
            title={
                !props.editCamera?
                <div className="silo-modal-title">
                    <Input id ='input-field-camera-name' disabled={!editCameraName} className="silo-camera-name-input" style={{width:(props?.cameraDisplayName?.length+1)*11+'px'}} bordered={false} value={!editCameraName ? props?.cameraDisplayName : cameraName} 
                    onChange={(event) => {
                        setCameraName(event.target.value);
                        document.getElementById('input-field-camera-name').style.width = (document.getElementById('input-field-camera-name').value.length+1)*10 +'px'
                    }}
                    />
                    {
                        editCameraName ?
                        <Button onClick={()  => {props.setCameraDisplayName(cameraName); setEditCameraName(false)}}>
                            Save
                        </Button>
                        :
                            <EditOutlined className="silo-edit-icon" onClick={() => {setEditCameraName(true); setCameraName(props.cameraDisplayName)}} />
                    }   
                </div>
                :
                <span className='manrope f-16'>
                    {props.cameraDisplayName}
                </span>
            }
        >
            <div className='silo-tool-container-modal'>
                <div id='silo-inspector-modal' className={props.loadingProgress ? "silo-inspector-modal-container loading" : "silo-inspector-modal-container"} data_tut="reactour__dragrotate">
                    <div id = 'silo-inspector-child' className="silo-inspector-child">
                        <Row id ='row-controls-silo' type="flex" className="silo-top-controls-row">
                            <div id='controls-row-silo' className="silo-bottom-controls-container">

                                <Popover placement="top" content={"Grid"}>
                                    <span
                                        className={`silo-control-icon ${isGridActive ? 'active' : ''}`}
                                        onClick={() => {
                                            setIsGridActive(!isGridActive);
                                            props.showGridForSilos();
                                        }}>
                                        <img src="/img/grid-silo.png" className="silo-bottom-icon-img" alt="Grid" />
                                    </span>
                                </Popover>

                                <div className="silo-bottom-separator" />

                                <Popover placement="top" content={"Safe Zone"}>
                                    <span
                                        className={`silo-control-icon ${activeControl === 'safeZone' ? 'active' : ''}`}
                                        onClick={() => {
                                            setActiveControl('safeZone');
                                            props.OpenOverlaySettingsModal();
                                            setTimeout(() => setActiveControl(null), 500);
                                        }}>
                                        <img src="/img/safe-zone-silo.png" className="silo-bottom-icon-img" alt="Safe Zone" />
                                    </span>
                                </Popover>

                                <div className="silo-bottom-separator" />

                                <Popover placement="top" content={"Transform"}>
                                    <span
                                        className={`silo-control-icon ${showScalePanel ? 'active' : ''}`}
                                        onClick={handleToggleTransformPanel}>
                                        <img src="/img/pan_default_1.png" className="silo-bottom-icon-img" alt="Transform" />
                                    </span>
                                </Popover>

                                <div className="silo-bottom-separator" />

                                <Popover placement="top" content={"Vertical Camera Align"}>
                                    <span
                                        className={`silo-control-icon ${activeControl === 'cameraAlign' ? 'active' : ''}`}
                                        onClick={() => {
                                            setActiveControl('cameraAlign');
                                            props.alignCameraForSilos();
                                            setTimeout(() => setActiveControl(null), 500);
                                        }}>
                                        <img src="/img/vertical-align-silo.png" className="silo-bottom-icon-img" alt="Vertical Camera Align" />
                                    </span>
                                </Popover>

                                <div className="silo-bottom-separator" />

                                <Popover placement="top" content={"Camera Controls"}>
                                    <span
                                        className={`silo-control-icon ${props.cameraSelected ? 'active' : ''}`}
                                        data_tut="reactour__cameraheight"
                                        onClick={() => {
                                            if (showScalePanel) {
                                                setShowScalePanel(false);
                                                if (props.inspector) {
                                                    props.inspector.disableTransformControls();
                                                }
                                            }
                                            props.changeCameraState();
                                        }}>
                                        <img src="/img/camera-silo.png" className="silo-bottom-icon-img" alt="Camera Controls" />
                                    </span>
                                </Popover>

                            </div>
                        </Row>
                        <div id = "grid">
                                <img id="grid-snapshot" src={require("../../../assets/images/grid.png")} className="silo-grid-snapshot" />
                        </div>
                        <div id="overlay-blur-top" className='overlay-blur'>
                        </div>
                        <div id="overlay-blur-bottom" className='overlay-blur'>
                        </div>
                        <div id="overlay-blur-left" className='overlay-blur'>
                        </div>
                        <div id="overlay-blur-right" className='overlay-blur'>
                        </div>
                    </div>
                
                </div>
                
                {/* Transform Panel */}
                {showScalePanel && (
                    <div className="silo-scale-panel" onMouseUp={(e) => e.stopPropagation()}>
                        <div className="silo-scale-panel-header">
                            <span className="silo-scale-panel-title">Transform</span>
                            <Button
                                size="small"
                                onClick={handleResetTransform}
                            >
                                Reset All
                            </Button>
                        </div>

                        {/* Transform Mode Toggle */}
                        <div className="silo-free-mode-toggle">
                            <TripleToggleSwitch
                                key={switchKey}
                                labels={transformLabels}
                                toggleState={transformMode}
                                onChange={handleTransformModeChange}
                            />
                        </div>

                        {/* X, Y, Z Controls - shown for all modes */}
                        <div className="silo-transform-section">
                            {/* Position Mode */}
                            {transformMode === 'left' && (
                                <>
                                    <div className="silo-transform-section-title-with-pin">
                                        <span className="silo-transform-section-title">Position</span>
                                        <Button
                                            size="small"
                                            onClick={handleResetPosition}
                                        >
                                            Reset
                                        </Button>
                                    </div>
                                    <div className="silo-scale-slider-container">
                                        <div className="silo-scale-slider-row">
                                            <span className="silo-scale-axis-label">X</span>
                                            <input type="number" step={0.1} value={positionValues.x} onFocus={onTransformInputFocus} onKeyDown={(e) => e.nativeEvent.stopImmediatePropagation()} onChange={(e) => { const value = parseFloat(e.target.value); handlePositionChange('x', isNaN(value) ? 0 : value); }} onBlur={onTransformInputBlur} className="silo-scale-input" />
                                            <span className="silo-input-unit">in</span>
                                            <div className="silo-input-arrows">
                                                <CaretUpOutlined className="silo-arrow-btn" onClick={() => handlePositionChange('x', parseFloat((positionValues.x + 0.1).toFixed(1)))} />
                                                <CaretDownOutlined className="silo-arrow-btn" onClick={() => handlePositionChange('x', parseFloat((positionValues.x - 0.1).toFixed(1)))} />
                                            </div>
                                        </div>
                                        <div className="silo-scale-slider-row">
                                            <span className="silo-scale-axis-label">Y</span>
                                            <input type="number" step={0.1} value={positionValues.y} onFocus={onTransformInputFocus} onKeyDown={(e) => e.nativeEvent.stopImmediatePropagation()} onChange={(e) => { const value = parseFloat(e.target.value); handlePositionChange('y', isNaN(value) ? 0 : value); }} onBlur={onTransformInputBlur} className="silo-scale-input" />
                                            <span className="silo-input-unit">in</span>
                                            <div className="silo-input-arrows">
                                                <CaretUpOutlined className="silo-arrow-btn" onClick={() => handlePositionChange('y', parseFloat((positionValues.y + 0.1).toFixed(1)))} />
                                                <CaretDownOutlined className="silo-arrow-btn" onClick={() => handlePositionChange('y', parseFloat((positionValues.y - 0.1).toFixed(1)))} />
                                            </div>
                                        </div>
                                        <div className="silo-scale-slider-row">
                                            <span className="silo-scale-axis-label">Z</span>
                                            <input type="number" step={0.1} value={positionValues.z} onFocus={onTransformInputFocus} onKeyDown={(e) => e.nativeEvent.stopImmediatePropagation()} onChange={(e) => { const value = parseFloat(e.target.value); handlePositionChange('z', isNaN(value) ? 0 : value); }} onBlur={onTransformInputBlur} className="silo-scale-input" />
                                            <span className="silo-input-unit">in</span>
                                            <div className="silo-input-arrows">
                                                <CaretUpOutlined className="silo-arrow-btn" onClick={() => handlePositionChange('z', parseFloat((positionValues.z + 0.1).toFixed(1)))} />
                                                <CaretDownOutlined className="silo-arrow-btn" onClick={() => handlePositionChange('z', parseFloat((positionValues.z - 0.1).toFixed(1)))} />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Rotation Mode */}
                            {transformMode === 'center' && (
                                <>
                                    <div className="silo-transform-section-title-with-pin">
                                        <span className="silo-transform-section-title">Rotation</span>
                                        <Button
                                            size="small"
                                            onClick={handleResetRotation}
                                        >
                                            Reset
                                        </Button>
                                    </div>
                                    <div className="silo-scale-slider-container">
                                        <div className="silo-scale-slider-row">
                                            <span className="silo-scale-axis-label">X</span>
                                            <input type="number" min={0} max={360} value={rotationValues.x} onFocus={onTransformInputFocus} onKeyDown={(e) => e.nativeEvent.stopImmediatePropagation()} onChange={(e) => { const value = parseInt(e.target.value); handleRotationChange('x', isNaN(value) ? 0 : value); }} onBlur={onTransformInputBlur} className="silo-scale-input" />
                                            <span className="silo-input-unit">°</span>
                                            <div className="silo-input-arrows">
                                                <CaretUpOutlined className="silo-arrow-btn" onClick={() => handleRotationChange('x', Math.min(360, rotationValues.x + 1))} />
                                                <CaretDownOutlined className="silo-arrow-btn" onClick={() => handleRotationChange('x', Math.max(0, rotationValues.x - 1))} />
                                            </div>
                                        </div>
                                        <div className="silo-scale-slider-row">
                                            <span className="silo-scale-axis-label">Y</span>
                                            <input type="number" min={0} max={360} value={rotationValues.y} onFocus={onTransformInputFocus} onKeyDown={(e) => e.nativeEvent.stopImmediatePropagation()} onChange={(e) => { const value = parseInt(e.target.value); handleRotationChange('y', isNaN(value) ? 0 : value); }} onBlur={onTransformInputBlur} className="silo-scale-input" />
                                            <span className="silo-input-unit">°</span>
                                            <div className="silo-input-arrows">
                                                <CaretUpOutlined className="silo-arrow-btn" onClick={() => handleRotationChange('y', Math.min(360, rotationValues.y + 1))} />
                                                <CaretDownOutlined className="silo-arrow-btn" onClick={() => handleRotationChange('y', Math.max(0, rotationValues.y - 1))} />
                                            </div>
                                        </div>
                                        <div className="silo-scale-slider-row">
                                            <span className="silo-scale-axis-label">Z</span>
                                            <input type="number" min={0} max={360} value={rotationValues.z} onFocus={onTransformInputFocus} onKeyDown={(e) => e.nativeEvent.stopImmediatePropagation()} onChange={(e) => { const value = parseInt(e.target.value); handleRotationChange('z', isNaN(value) ? 0 : value); }} onBlur={onTransformInputBlur} className="silo-scale-input" />
                                            <span className="silo-input-unit">°</span>
                                            <div className="silo-input-arrows">
                                                <CaretUpOutlined className="silo-arrow-btn" onClick={() => handleRotationChange('z', Math.min(360, rotationValues.z + 1))} />
                                                <CaretDownOutlined className="silo-arrow-btn" onClick={() => handleRotationChange('z', Math.max(0, rotationValues.z - 1))} />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Scaling Mode */}
                            {transformMode === 'right' && (
                                <>
                                    <div className="silo-transform-section-title-with-pin">
                                        <span className="silo-transform-section-title">Scaling</span>
                                        <div className="silo-scale-header-actions">
                                            <Tooltip title={<span className='manrope f-12 white'>Pin to uniformly scale product in all dimensions</span>}>
                                                <img className='silo-pin-icon' onClick={() => setUniformScale(!uniformScale)} src={uniformScale ? require("../../../assets/images/pin-controls-selected.svg") : require("../../../assets/images/pin-controls.svg")} alt="pin" />
                                            </Tooltip>
                                            <Button
                                                size="small"
                                                onClick={handleResetScale}
                                            >
                                                Reset
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="silo-scale-slider-container">
                                        <div className="silo-scale-slider-row">
                                            <span className="silo-scale-axis-label">X</span>
                                            <Slider min={0} max={200} value={scaleValues.x} onChange={(value) => handleScaleChange('x', value)} />
                                            <input type="number" min={0} max={200} value={scaleValues.x} onFocus={() => { isInputFocusedRef.current = true; }} onChange={(e) => { const value = parseInt(e.target.value); if (!isNaN(value)) { handleScaleChange('x', value); } }} onBlur={(e) => { isInputFocusedRef.current = false; const value = parseInt(e.target.value); if (isNaN(value) || value < 0) { handleScaleChange('x', 0); } else if (value > 200) { handleScaleChange('x', 200); } }} className="silo-scale-input silo-scale-input-scale" />
                                        </div>
                                        <div className="silo-scale-slider-row">
                                            <span className="silo-scale-axis-label">Y</span>
                                            <Slider min={0} max={200} value={scaleValues.y} onChange={(value) => handleScaleChange('y', value)} disabled={uniformScale} />
                                            <input type="number" min={0} max={200} value={scaleValues.y} onFocus={() => { isInputFocusedRef.current = true; }} onChange={(e) => { const value = parseInt(e.target.value); if (!isNaN(value)) { handleScaleChange('y', value); } }} onBlur={(e) => { isInputFocusedRef.current = false; const value = parseInt(e.target.value); if (isNaN(value) || value < 0) { handleScaleChange('y', 0); } else if (value > 200) { handleScaleChange('y', 200); } }} className="silo-scale-input silo-scale-input-scale" disabled={uniformScale} />
                                        </div>
                                        <div className="silo-scale-slider-row">
                                            <span className="silo-scale-axis-label">Z</span>
                                            <Slider min={0} max={200} value={scaleValues.z} onChange={(value) => handleScaleChange('z', value)} disabled={uniformScale} />
                                            <input type="number" min={0} max={200} value={scaleValues.z} onFocus={() => { isInputFocusedRef.current = true; }} onChange={(e) => { const value = parseInt(e.target.value); if (!isNaN(value)) { handleScaleChange('z', value); } }} onBlur={(e) => { isInputFocusedRef.current = false; const value = parseInt(e.target.value); if (isNaN(value) || value < 0) { handleScaleChange('z', 0); } else if (value > 200) { handleScaleChange('z', 200); } }} className="silo-scale-input silo-scale-input-scale" disabled={uniformScale} />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
                
                <SiloSettings border={true} {...props}/>
            </div>
        </Modal>
    </div>
  )
}
